import React, { useMemo } from "react";
import { Button, Card, Empty, Space } from "antd";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./index.modules.less";

const contentMap = {
  union_not_open: {
    title: "您暂无联运权限",
    description: "如果有联运合作诉求，请联系主账号管理员或商务同学开通。",
    buttonText: "返回首页",
    targetPath: "/view/home",
    subTitle: "当前账号暂未开通联运后台能力",
    tone: "warning",
  },
  no_permission: {
    title: "当前身份暂无业务模块权限",
    description: "您已登录联运后台，但当前身份未被授予任何业务模块权限，请联系主账号管理员开通。",
    buttonText: "返回首页",
    targetPath: "/view/home",
    subTitle: "当前身份存在，但暂无可进入的业务模块",
    tone: "neutral",
  },
  identity_not_selected: {
    title: "请先选择登录身份",
    description: "当前账号存在多个可用身份，需先完成身份选择后再访问业务页面。",
    buttonText: "去选择身份",
    targetPath: "/view/identity_select",
    subTitle: "登录后需先确认本次进入的厂商身份",
    tone: "primary",
  },
  identity_invalid: {
    title: "当前身份已失效",
    description: "当前身份可能已被禁用、移除或失效，请重新选择可用身份后继续。",
    buttonText: "重新选择身份",
    targetPath: "/view/identity_select",
    subTitle: "请切换到可用身份后继续访问联运后台",
    tone: "danger",
  },
};

const NoAccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const permission = useSelector((state) => state.permission);
  const params = new URLSearchParams(location.search);
  const scene = params.get("scene") || "union_not_open";
  const normalizedScene = scene === "no_permission" && permission.identityType === "owner" ? "union_not_open" : scene;
  const current = contentMap[normalizedScene] || contentMap.union_not_open;

  if (!contentMap[normalizedScene]) {
    console.warn(`[NoAccessPage] unknown scene: "${scene}", falling back to union_not_open`);
  }

  const primaryTargetPath = useMemo(() => {
    if (normalizedScene !== "identity_invalid") {
      return current.targetPath;
    }

    const hasAvailableIdentity = Array.isArray(permission.identityList) && permission.identityList.length > 0;

    if (hasAvailableIdentity) {
      return "/view/identity_select";
    }

    return permission.isUnionUser ? "/view/home" : "/view/no_access?scene=union_not_open";
  }, [current.targetPath, normalizedScene, permission.identityList, permission.isUnionUser]);

  const secondaryTargetPath = useMemo(() => {
    if (normalizedScene === "identity_not_selected") {
      return "/view/home";
    }

    if (normalizedScene === "identity_invalid") {
      const hasAvailableIdentity = Array.isArray(permission.identityList) && permission.identityList.length > 0;
      return hasAvailableIdentity ? "/view/home" : "/view/no_access?scene=union_not_open";
    }

    return "/view/home";
  }, [normalizedScene, permission.identityList]);

  const secondaryButtonText = normalizedScene === "identity_invalid" && secondaryTargetPath === "/view/no_access?scene=union_not_open" ? "查看未开通说明" : "回到首页";

  const showSecondaryButton = primaryTargetPath !== secondaryTargetPath;

  return (
    <div className={styles.page}>
      <Card bordered={false} className={styles.card}>
        <div className={styles.hero}>
          <div className={styles.heroInner}>
            <div className={`${styles.badge} ${styles[`badge${current.tone.charAt(0).toUpperCase()}${current.tone.slice(1)}`]}`}>
              联运后台访问提示
            </div>
            <div className={styles.subTitle}>{current.subTitle}</div>
          </div>
        </div>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={(
            <div className={styles.description}>
              <div className={styles.title}>{current.title}</div>
              <div className={styles.text}>{current.description}</div>
            </div>
          )}
        />
        <Space className={styles.actions} size={12}>
          <Button type="primary" onClick={() => navigate(primaryTargetPath)}>
            {current.buttonText}
          </Button>
          {showSecondaryButton && (
            <Button onClick={() => navigate(secondaryTargetPath)}>{secondaryButtonText}</Button>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default NoAccessPage;
